import React from 'react'
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Button
} from '@mui/material'
import {
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material'

export interface RealtorData {
  id?: string
  image: string
  name: string
  title: string
  description: string
  phone: string
  email: string
  url?: string
}

export interface RealtorCardProps {
  data: RealtorData
  onLearnMore?: (realtor: RealtorData) => void
}

const RealtorCard: React.FC<RealtorCardProps> = ({
  data,
  onLearnMore
}) => {
  const {
    image,
    name,
    title,
    description,
    phone,
    email
  } = data

  return (
    <Card
      sx={{
        maxWidth: 400,
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          '& .realtor-image': {
            transform: 'scale(1.05)'
          }
        }
      }}
    >
      {/* Image Section with Overlay */}
      <Box sx={{ 
        position: 'relative', 
        overflow: 'hidden',
        backgroundColor: '#f0f0f0'
      }}>
        <CardMedia
          component="img"
          height="280"
          image={image}
          alt={name}
          className="realtor-image"
          sx={{
            objectFit: 'cover',
            width: '100%',
            transition: 'transform 0.3s ease',
            transformOrigin: 'center center'
          }}
        />
        
        {/* Dark gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '60%',
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '20px',
            pointerEvents: 'none',
            zIndex: 1
          }}
        >
          {/* Name */}
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: 'bold',
              color: '#ffffff',
              textAlign: 'center',
              fontSize: '2rem',
              marginBottom: '4px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
            }}
          >
            {name}
          </Typography>

          {/* Title */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: '#b08b4f',
              textAlign: 'center',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <CardContent
        sx={{
          backgroundColor: '#0e2340',
          color: '#ffffff',
          padding: '24px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&:last-child': {
            paddingBottom: '24px'
          }
        }}
      >
        {/* Content wrapper */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: '#ffffff',
              textAlign: 'center',
              marginBottom: '20px',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              fontFamily: 'Montserrat, sans-serif',
              flex: 1
            }}
          >
            {description}
          </Typography>

          {/* Contact Information */}
          <Box sx={{ marginBottom: '20px' }}>
            {/* Phone */}
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <PhoneIcon sx={{ fontSize: '18px', marginRight: '8px', color: '#ffffff' }} />
              <Typography
                variant="body2"
                sx={{
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 'bold'
                }}
              >
                {phone}
              </Typography>
            </Box>

            {/* Email */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ fontSize: '18px', marginRight: '8px', color: '#ffffff' }} />
              <Typography
                variant="body2"
                sx={{
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 'bold'
                }}
              >
                {email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Learn More Button */}
        <Button
          onClick={() => {
              console.log('data.url',data)
              console.log('data.url',data.url)
              window.open(data.url, '_blank')
          }}
          sx={{
            width: '100%',
            backgroundColor: '#b08b4f',
            color: '#ffffff',
            borderRadius: '8px',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: 700,
            textTransform: 'uppercase',
            fontFamily: 'Montserrat, sans-serif',
            letterSpacing: '0.5px',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#b08b4f',
              border: '2px solid #b08b4f',
            }
          }}
        >
          Learn More
        </Button>
      </CardContent>
    </Card>
  )
}

export default RealtorCard
